import axios from "axios";
import { APP_PATH } from "../api/backendPath";

/**
 * Access-token helpers for long-lived Socket.IO connections.
 *
 * The gateway authenticates a socket from the JWT in its handshake query, once per connection.
 * A socket that reconnects (network blip, deploy, idle timeout) after the JWT expired is refused
 * by the gateway middleware, and socket.io does not retry a refused handshake on its own, so the
 * socket has to refresh the token and reconnect itself. These helpers read sessionStorage at call
 * time (unlike useRefreshToken, which captures the tokens when the component renders).
 */

const ACCESS_KEY = "usr_1a2b3c";
const REFRESH_KEY = "usr_4d5e6f";

export const getAccessToken = () => sessionStorage.getItem(ACCESS_KEY);

const decodeJwt = (token) => {
  try {
    const part = String(token).split(".")[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch (e) {
    return null;
  }
};

/** User id carried by the access token (null when absent/undecodable). */
export const getTokenUserId = (token = getAccessToken()) => {
  const p = token ? decodeJwt(token) : null;
  return p ? (p.userId ?? p.user_id ?? p.sub ?? p.id ?? null) : null;
};

/** True while the token is decodable and not within `skewMs` of its expiry. */
export const isTokenFresh = (token, skewMs = 15000) => {
  const p = token ? decodeJwt(token) : null;
  if (!p) return false;
  if (!p.exp) return true;
  return p.exp * 1000 - skewMs > Date.now();
};

/** Handshake refusals from the gateway's auth middleware ("Authentication failed", jwt expired...). */
export const isAuthError = (err) => {
  const msg = String(err?.message || err || "").toLowerCase();
  return ["jwt", "expired", "auth", "token", "unauthor"].some((k) =>
    msg.includes(k),
  );
};

let inflight = null;

/**
 * Exchange the refresh token for a new access token and persist both. Single-flight: concurrent
 * callers share one request, so several sockets reconnecting together rotate the token once.
 * Resolves to the new access token, or null when there is no session or the refresh failed.
 */
export const refreshAccessToken = () => {
  if (inflight) return inflight;
  inflight = (async () => {
    const access = sessionStorage.getItem(ACCESS_KEY);
    const refresh = sessionStorage.getItem(REFRESH_KEY);
    if (!refresh) return null;
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}${APP_PATH.refreshToken}`,
      { refresh_token: refresh },
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${access}`,
          "User-Refresh-Token": `${refresh}`,
          "Content-Type": "application/json",
        },
      },
    );
    const tokens = res?.data?.data;
    if (!tokens?.access_token) return null;
    // A logout while the request was in flight must not resurrect the session.
    if (!sessionStorage.getItem(REFRESH_KEY)) return null;
    sessionStorage.setItem(ACCESS_KEY, tokens.access_token);
    if (tokens.refresh_token)
      sessionStorage.setItem(REFRESH_KEY, tokens.refresh_token);
    return tokens.access_token;
  })()
    .catch(() => null)
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

/**
 * A token to (re)open a socket with: the stored one while it is valid, otherwise a refreshed one.
 * `rejected` is the token the gateway just refused; it is never handed back.
 */
export const ensureFreshAccessToken = async (rejected = null) => {
  const current = getAccessToken();
  if (current && current !== rejected && isTokenFresh(current)) return current;
  return refreshAccessToken();
};
